import {
  Box,
  Grid2 as Grid,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import PricingCard from "../components/auth/registerSteps/PricingCard";
import PopupInfo from "./auth/registerSteps/PopupInfo";

const ChangePlan = ({
  checked,
  defPlan,
  selectedPlan,
  setSelectedPlan,
  tiers,
  contractorList,
  mobileSize,
  tabletSize,
  trial,
  currentUser,
  loading,
  setNotaryError,
  notaryError,
}) => {
  const [showAlert, setShowAlert] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [infoIndex, setInfoIndex] = useState(0);
  const [cardTitle, setCardTitle] = useState("");

  const displayTiers = useMemo(
    () =>
      (tiers || []).filter(
        (plan) =>
          plan?.title &&
          plan.title !== "Private Provider" &&
          plan?.hidden !== true,
      ),
    [tiers],
  );

  // Count total notary users (main user + subusers)
  const currentNotaryCount = useMemo(() => {
    let count = 0;

    // Check if main user is a notary
    if (currentUser?.user?.notary_user?.is_notary) {
      count++;
    }

    // Count notary subusers
    if (currentUser?.subusers_row) {
      const notarySubusers = currentUser.subusers_row.filter(
        (subuser) => subuser?.notary_user?.is_notary,
      );
      count += notarySubusers.length;
    }

    return count;
  }, [currentUser]);

  const handleDisable = (tierTitle) => {
    const checkedSatus = defPlan.interval === "month" ? false : true;

    if (
      tierTitle === defPlan.title &&
      checkedSatus === checked &&
      !trial?.has_trial
    ) {
      return true;
    }
  };

  useEffect(() => {
    if (showAlert) {
      // First timeout to start the fade-out effect after 4500ms
      const timeout = setTimeout(() => {
        setIsFadingOut(true); // Start fading out

        // Second timeout to hide the alert and reset isFadingOut after the fade-out duration
        const fadeOutTimeout = setTimeout(() => {
          setShowAlert(false); // Hide alert after fade-out completes
          setIsFadingOut(false); // Reset fading state
        }, 1000); // This duration should match the CSS fade-out duration

        return () => clearTimeout(fadeOutTimeout);
      }, 3500); // Show the alert for 5 seconds minus fade duration

      return () => clearTimeout(timeout);
    }
  }, [showAlert]);

  // Validate notary count when plan is selected
  useEffect(() => {
    if (selectedPlan && setNotaryError) {
      let newPlanNotaryLimit = selectedPlan?.notary_user?.limit || 0;

      if (String(newPlanNotaryLimit ?? "").toLowerCase() === "unlimited") {
        newPlanNotaryLimit = 9999999999999;
      }
      if (currentNotaryCount > newPlanNotaryLimit) {
        const notariesToRemove = currentNotaryCount - newPlanNotaryLimit;
        const errorMessage =
          newPlanNotaryLimit === 0
            ? `You currently have ${currentNotaryCount} notary user${
                currentNotaryCount > 1 ? "s" : ""
              }. The selected plan does not allow notary users. Please cancel all ${currentNotaryCount} notary subscription${
                currentNotaryCount > 1 ? "s" : ""
              } before proceeding.`
            : `You currently have ${currentNotaryCount} notary user${
                currentNotaryCount > 1 ? "s" : ""
              }, but the selected plan only allows ${newPlanNotaryLimit}. Please cancel ${notariesToRemove} notary subscription${
                notariesToRemove > 1 ? "s" : ""
              } before proceeding.`;
        setNotaryError(errorMessage);
      } else {
        setNotaryError("");
      }
    }
  }, [selectedPlan, currentNotaryCount, setNotaryError]);

  return (
    <>
      <Box sx={{ height: "100%" }}>
        <Grid container spacing={2} mb={2}>
          <Grid size={{ xs: 12 }}>
            <Typography
              mt={0}
              color="primary"
              align="center"
              variant={mobileSize ? "h6" : tabletSize ? "h5" : "h3"}
            >
              Choose your new plan
            </Typography>
          </Grid>
          {notaryError && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error" sx={{ mx: "auto", maxWidth: "800px" }}>
                {notaryError}
              </Alert>
            </Grid>
          )}
        </Grid>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              backgroundColor: "#fff",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              zIndex: 9999,
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        ) : (
          <Grid
            container
            spacing={2}
            mt={0}
            sx={{ justifyContent: "center", width: "100%" }}
          >
            {displayTiers.map((plan, index) => (
              <Grid
                size={{ lg: 3, md: 3.8, sm: 5.8, xs: 11.3 }}
                key={
                  plan?.product_id ||
                  plan?.yearly_price_id ||
                  plan?.monthly_price_id ||
                  plan?.title ||
                  index
                }
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <PricingCard
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  planOptions={plan.features}
                  title={plan?.title || ""}
                  new_title={plan?.new_title || ""}
                  price={
                    checked ? plan?.yearly_price : plan?.monthly_price || ""
                  }
                  checked={checked}
                  plan={plan}
                  trial={trial}
                  contractorList={contractorList}
                  showAlert={showAlert}
                  setShowAlert={setShowAlert}
                  isFadingOut={isFadingOut}
                  type="change"
                  handleDisable={handleDisable}
                  currentUser={currentUser}
                  setShowInfo={setShowInfo}
                  setInfoIndex={setInfoIndex}
                  cardIndex={index}
                  setCardTitle={setCardTitle}
                />
              </Grid>
            ))}
          </Grid>
        )}

        <PopupInfo
          open={showInfo}
          setOpen={setShowInfo}
          currentIndex={infoIndex}
          setInfoIndex={setInfoIndex}
          cardTitle={cardTitle}
        />
      </Box>
    </>
  );
};

export default ChangePlan;
